import React, { useState } from 'react';
import { Placeholder } from 'react-bootstrap';
import { I18n } from 'aws-amplify/utils';
import {
  FormSection,
  FormField,
  SectionHeader,
  SectionBody,
  SectionFooter,
  Button,
  Link,
  Hint,
  Input,
  SectionFooterPrimaryContent,
  SectionFooterSecondaryContent,
} from './auth-ui';

const overlay = (
  <div id="loading">
    <Placeholder animation="wave">
      <Placeholder xs={12} />
    </Placeholder>
  </div>
);

// Previously a subclass of aws-amplify-react's `SignIn`, which called
// `Auth.signIn` itself and then handed the result to UserProvider through
// `confirmLogin`. Signing in is now UserProvider's `login` alone: the old split
// meant a failed sign-in still ran `confirmLogin(null)` from a `finally` block,
// which threw reading `null.username`.
function CustomSignIn({ theme, authData = {}, changeState, onError, login }) {
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState(authData.username || '');
  const [password, setPassword] = useState('');

  const signIn = async (event) => {
    if (event) {
      event.preventDefault();
    }
    setLoading(true);
    try {
      const { isSignedIn, nextStep } = await login(username, password);
      if (isSignedIn) {
        // UserProvider has set the user, so App is about to render the box in
        // place of this form. Leaving the overlay up avoids a flash of the
        // enabled form and a setState on an unmounted component.
        return;
      }
      setLoading(false);
      switch (nextStep.signInStep) {
        case 'CONFIRM_SIGN_UP':
          changeState('confirmSignUp', { username });
          break;
        case 'RESET_PASSWORD':
          changeState('forgotPassword', { username });
          break;
        default:
          // MFA is off on this user pool and there is no admin-created-user
          // flow, so the remaining challenges (SMS/TOTP codes, forced password
          // change, TOTP setup) are unreachable today and have no UI. Say so
          // rather than failing silently if the pool config ever changes.
          onError(
            `Sign-in needs a step this app does not implement: ${nextStep.signInStep}`
          );
      }
    } catch (err) {
      setLoading(false);
      onError(err);
    }
  };

  return (
    <FormSection theme={theme} data-test="sign-in-section">
      {loading ? overlay : null}
      <SectionHeader theme={theme} data-test="sign-in-header-section">
        {I18n.get('Sign in to your account')}
      </SectionHeader>
      <form onSubmit={signIn}>
        <SectionBody theme={theme}>
          <FormField theme={theme}>
            <Input
              value={username}
              autoFocus={true}
              placeholder={I18n.get('Enter your username')}
              theme={theme}
              name="username"
              onChange={(e) => setUsername(e.target.value)}
              data-test="username-input"
              autoCapitalize="off"
              disabled={loading}
            />
          </FormField>
          <FormField theme={theme}>
            <Input
              value={password}
              placeholder={I18n.get('Enter your password')}
              theme={theme}
              type="password"
              name="password"
              onChange={(e) => setPassword(e.target.value)}
              data-test="sign-in-password-input"
              disabled={loading}
            />
            <Hint theme={theme}>
              {I18n.get('Forget your password? ')}
              <Link
                theme={theme}
                onClick={() => changeState('forgotPassword', { username })}
                data-test="sign-in-forgot-password-link"
              >
                {I18n.get('Reset password')}
              </Link>
            </Hint>
          </FormField>
        </SectionBody>
        <SectionFooter theme={theme} data-test="sign-in-footer-section">
          <SectionFooterPrimaryContent theme={theme}>
            <Button
              theme={theme}
              type="submit"
              disabled={loading}
              data-test="sign-in-sign-in-button"
            >
              {I18n.get('Sign In')}
            </Button>
          </SectionFooterPrimaryContent>
          <SectionFooterSecondaryContent theme={theme}>
            {I18n.get('No account? ')}
            <Link
              theme={theme}
              onClick={() => changeState('signUp')}
              data-test="sign-in-create-account-link"
            >
              {I18n.get('Create account')}
            </Link>
          </SectionFooterSecondaryContent>
        </SectionFooter>
      </form>
    </FormSection>
  );
}

export default CustomSignIn;
